// Pure formatting for Preference Logs history (optinlogs).
// v2 records carry schemaVersion + before/after snapshots; legacy records
// have no schemaVersion and their stored tag arrays are not a known state.

const NOT_RECORDED = 'Not recorded';

export const CHANNELS = [
  { key: 'emailTags', label: 'Email' },
  { key: 'textTags', label: 'Text' },
  { key: 'tempTags', label: 'Pending SMS' },
];

const LEGACY_CHANNELS = CHANNELS.slice(0, 2);

const EVENT_LABELS = {
  preferences_changed: 'Preferences changed',
  sms_optin_requested: 'SMS opt-in requested',
  sms_optin_confirmed: 'SMS opt-in confirmed',
  sms_stopped: 'SMS stopped',
  sms_intro: 'SMS intro',
  sms_restart: 'SMS restart',
};

const SOURCE_LABELS = {
  preference_page: 'Preference page',
  staff_contact_edit: 'Staff contact edit',
  nonmember_signup: 'Non-member signup',
  sms_webhook: 'SMS webhook',
};

const ACTOR_LABELS = {
  preference_link: 'Preference link',
  staff: 'Staff',
  service: 'Service',
  system: 'System',
  sms_sender: 'SMS sender',
};

const unique = ids => Array.from(new Set(ids));

// ObjectId hex strings sort lexically in creation order
export const sortLogsNewestFirst = logs =>
  [...logs].sort((a, b) => {
    const idA = String(a?._id ?? '');
    const idB = String(b?._id ?? '');
    if (idA === idB) return 0;
    return idA < idB ? 1 : -1;
  });

export const isLegacy = log =>
  log?.schemaVersion === undefined || log?.schemaVersion === null;

// null means unknown (a side was not recorded), never "no change"
export const diffTopics = (before, after) => {
  if (!Array.isArray(before) || !Array.isArray(after)) return null;
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  return {
    added: unique(after).filter(id => !beforeSet.has(id)),
    removed: unique(before).filter(id => !afterSet.has(id)),
  };
};

export const getChannelChanges = log => {
  if (isLegacy(log)) return [];
  return CHANNELS.map(({ key, label }) => {
    const before = log.before?.[key];
    const after = log.after?.[key];
    const diff = diffTopics(before, after);
    return {
      key,
      label,
      recorded: diff !== null,
      added: diff ? diff.added : null,
      removed: diff ? diff.removed : null,
      after: Array.isArray(after) ? unique(after) : null,
    };
  });
};

export const getDisableTextingChange = log => {
  if (isLegacy(log)) return null;
  const before = log.before?.disableTexting;
  const after = log.after?.disableTexting;
  if (typeof before !== 'boolean' && typeof after !== 'boolean') return null;
  return { before, after, changed: before !== after };
};

// Legacy arrays: non-empty = recorded topics, empty = not recorded
export const getLegacyTopics = log =>
  LEGACY_CHANNELS.map(({ key, label }) => {
    const stored = log?.[key];
    return {
      key,
      label,
      topics: Array.isArray(stored) && stored.length ? unique(stored) : null,
    };
  });

export const resolveTopicName = (id, lookup) => lookup?.[id] || id;

export const eventLabel = log => {
  if (isLegacy(log)) return log?.status || NOT_RECORDED;
  return EVENT_LABELS[log.eventType] || log.eventType || log.status;
};

export const sourceLabel = log => {
  if (!log?.source) return NOT_RECORDED;
  return SOURCE_LABELS[log.source] || log.source;
};

export const actorLabel = log => {
  const type = log?.actor?.type;
  if (!type) return NOT_RECORDED;
  return ACTOR_LABELS[type] || type;
};

export const disableTextingLabel = change =>
  change.after ? 'Texting disabled' : 'Texting enabled';

export const summarizeChanges = log => {
  if (isLegacy(log)) return 'Before not recorded';

  const parts = [];
  const textingChange = getDisableTextingChange(log);
  if (textingChange?.changed) parts.push(disableTextingLabel(textingChange));

  const channels = getChannelChanges(log).filter(c => c.recorded);
  if (!channels.length) {
    parts.push('Topics not recorded');
    return parts.join('; ');
  }

  for (const { label, added, removed } of channels) {
    const counts = [];
    if (added.length) counts.push(`+${added.length}`);
    if (removed.length) counts.push(`-${removed.length}`);
    if (counts.length) parts.push(`${label} ${counts.join(' / ')}`);
  }
  return parts.length ? parts.join('; ') : 'No topic changes';
};

// An sms_optin_requested written by a save repeats that save's before/after,
// so its row points at the preferences_changed row instead of repeating it
const isPairedSmsRequest = (log, savedOperationIds) =>
  !isLegacy(log) &&
  log.eventType === 'sms_optin_requested' &&
  Boolean(log.operationId) &&
  savedOperationIds.has(log.operationId);

export const buildEventRows = logs => {
  if (!Array.isArray(logs)) return [];
  const savedOperationIds = new Set(
    logs
      .filter(
        log =>
          !isLegacy(log) &&
          log.eventType === 'preferences_changed' &&
          log.operationId
      )
      .map(log => log.operationId)
  );
  return sortLogsNewestFirst(logs).map(log => ({
    log,
    legacy: isLegacy(log),
    event: eventLabel(log),
    source: sourceLabel(log),
    actor: actorLabel(log),
    summary: isPairedSmsRequest(log, savedOperationIds)
      ? 'Part of the same save'
      : summarizeChanges(log),
  }));
};
